import type {
  SoftReceivedLotFormValues,
  SoftReceivedPalletRowFormValues,
  SoftReceivedPurchaseOrderLineFormValues,
  SoftReceivedPurchaseOrdersFormValues,
} from "../../schemas"

import type { SoftReceivedGoodsReceiptFormValues } from "./schemas"
import type { SoftReceivedLotReference } from "./types"

import { HOLD_AREA_1 } from "@/constants/warehouse"
import type {
  GoodsReceipt,
  GoodsReceiptPalletRowLotUpdateInput,
  GoodsReceiptPalletRowUpdateInput,
  GoodsReceiptUpdateInput,
  OrderLine,
  Product,
} from "@/graphql/codegen/graphql"

export interface CheckInValidationResult {
  isValid: boolean
  errors: string[]
}

export const getSoftReceivedGoodsReceiptDefaultFormValues = (
  goodsReceipt: GoodsReceipt
): SoftReceivedGoodsReceiptFormValues => {
  return {
    trackingNumbers: goodsReceipt?.trackingNumbers?.length ? goodsReceipt.trackingNumbers : [""],
    destination: HOLD_AREA_1,
    palletQtyDelivered: goodsReceipt?.softReceiveData?.deliveredPalletsCount ?? null,
    spacesUsed: goodsReceipt?.softReceiveData?.spacesUsed ?? null,
  }
}

export const serializeSoftReceivedGoodsReceiptForm = (
  goodsReceipt: GoodsReceipt,
  formValues: SoftReceivedGoodsReceiptFormValues
): GoodsReceiptUpdateInput => {
  return {
    goodsReceiptId: goodsReceipt.goodsReceiptId,
    warehouseId: goodsReceipt.warehouseArea?.warehouseAreaId as string,
    trackingNumbers: formValues.trackingNumbers?.filter((tn) => tn.trim() !== ""),
    softReceiveData: {
      deliveredPalletsCount: formValues.palletQtyDelivered,
      spacesUsed: formValues.spacesUsed,
    },
  }
}

const isLotFormValid = (lot: SoftReceivedLotFormValues): boolean => {
  return (
    lot.lotNumber.trim() !== "" &&
    lot.unitsCount !== null &&
    lot.quantity !== null &&
    lot.packageUnitQuantity !== null &&
    lot.packagingType !== null &&
    Number.isInteger(lot.unitsCount)
  )
}

const isPalletRowFormValid = (palletRow: SoftReceivedPalletRowFormValues): boolean => {
  if (palletRow.lots.length === 0) {
    return false
  }

  if (palletRow.palletsCount === null || palletRow.palletUnitQuantity === null) {
    return false
  }

  return palletRow.lots.every(isLotFormValid)
}

export const validateFinalizeCheckIn = (formValues: SoftReceivedPurchaseOrdersFormValues): CheckInValidationResult => {
  const errors: string[] = []

  if (formValues.purchaseOrders.length === 0) {
    errors.push("Please add at least one Purchase Order.")
    return { isValid: false, errors }
  }

  const linesWithoutPalletRows = formValues.purchaseOrders
    .flatMap((order) => order.lines)
    .filter((line) => line.palletRows.length === 0)

  if (linesWithoutPalletRows.length > 0) {
    errors.push("Please add at least one pallet row to each Purchase Order line.")
  }

  const palletRows = formValues.purchaseOrders.flatMap((order) => order.lines.flatMap((line) => line.palletRows))

  const hasFractionalUnitsCount = palletRows.some((palletRow) =>
    palletRow.lots.some((lot) => lot.unitsCount !== null && !Number.isInteger(lot.unitsCount))
  )

  if (hasFractionalUnitsCount) {
    errors.push("Partial packaging detected. Please update the packaging UoM to ensure a whole number of units.")
  }

  const hasInvalidPalletRows = palletRows.some((palletRow) => !isPalletRowFormValid(palletRow))

  if (hasInvalidPalletRows) {
    errors.push("Please fill in all required fields.")
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

const serializeSoftReceivedPalletRowLotFormValues = (
  lot: SoftReceivedLotFormValues
): GoodsReceiptPalletRowLotUpdateInput => ({
  goodsReceiptPalletRowLotId: lot.goodsReceiptPalletRowLotId ?? undefined,
  lotNumber: lot.lotNumber,
  batchNumber: lot.batchNumber || "",
  units: lot.unitsCount,
  totalUom: lot.quantity,
  packageUnitQuantity: lot.packageUnitQuantity?.toString() ?? null,
  packagingType: lot.packagingType,
})

const serializeSoftReceivedPalletRowFormValues = (
  goodsReceiptLineId: string,
  palletRow: SoftReceivedPalletRowFormValues
): GoodsReceiptPalletRowUpdateInput => ({
  goodsReceiptPalletRowId: palletRow.goodsReceiptPalletRowId ?? undefined,
  goodsReceiptProductDetailId: goodsReceiptLineId,
  palletUnitQuantity: palletRow.palletUnitQuantity?.toString() ?? null,
  palletQty: palletRow.palletsCount,
  lots: palletRow.lots.map((lot) => serializeSoftReceivedPalletRowLotFormValues(lot)),
})

export const serializeSoftReceivedPurchaseOrderForm = (
  formValues: SoftReceivedPurchaseOrdersFormValues
): GoodsReceiptPalletRowUpdateInput[] =>
  formValues.purchaseOrders.flatMap((purchaseOrder) =>
    purchaseOrder.lines.flatMap((line) =>
      line.palletRows.map((palletRow) => serializeSoftReceivedPalletRowFormValues(line.goodsReceiptLineId, palletRow))
    )
  )

const mapGoodsReceiptOrderLinesById = (goodsReceipt: GoodsReceipt): Map<string, OrderLine> => {
  return new Map(
    (goodsReceipt.orders ?? []).flatMap((order) =>
      (order.orderLines ?? []).map((orderLine) => [orderLine.orderLineId, orderLine] as const)
    )
  )
}

const getSoftReceivedLotReferencesFromPalletRow = (
  palletRow: SoftReceivedPalletRowFormValues,
  purchaseOrderId: number,
  product: Product
): SoftReceivedLotReference[] => {
  return palletRow.lots
    .filter((lot) => lot.lotNumber.trim() !== "")
    .map((lot) => ({
      purchaseOrderId,
      product,
      lotNumber: lot.lotNumber,
      batchNumber: lot.batchNumber,
    }))
}

const getSoftReceivedLotReferencesFromPurchaseOrderLine = (
  formPurchaseOrderLine: SoftReceivedPurchaseOrderLineFormValues,
  purchaseOrderId: number,
  purchaseOrderLineById: Map<string, OrderLine>
): SoftReceivedLotReference[] => {
  const purchaseOrderLine = purchaseOrderLineById.get(formPurchaseOrderLine.purchaseOrderLineId)

  if (!purchaseOrderLine) {
    return []
  }

  const product = purchaseOrderLine.product!

  return formPurchaseOrderLine.palletRows.flatMap((palletRow) =>
    getSoftReceivedLotReferencesFromPalletRow(palletRow, purchaseOrderId, product)
  )
}

export const getSoftReceivedLotReferencesFromFormValues = (
  formValues: SoftReceivedPurchaseOrdersFormValues,
  goodsReceipt: GoodsReceipt
): SoftReceivedLotReference[] => {
  const purchaseOrderLineById = mapGoodsReceiptOrderLinesById(goodsReceipt)

  return formValues.purchaseOrders.flatMap((formPurchaseOrder) =>
    formPurchaseOrder.lines.flatMap((formPurchaseOrderLine) =>
      getSoftReceivedLotReferencesFromPurchaseOrderLine(
        formPurchaseOrderLine,
        formPurchaseOrder.purchaseOrderId,
        purchaseOrderLineById
      )
    )
  )
}
